import type { BrandWithUrl } from '../../../models/Brand.ts';
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Card,
  Group,
  LoadingOverlay,
  Text,
} from '@mantine/core';
import { IconEdit } from '@tabler/icons-react';
import {
  getServiceColor,
  getServiceLabel,
} from '../../../const/service_type_mapping.ts';
import useBrandImage from '../../../hooks/brands/useBrandImage.ts';
import imageCache from '../../../utils/ImageCache.ts';
import { useDisclosure } from '@mantine/hooks';
import { UpdateBrandMetadataModal } from '../../modal/brands/UpdateBrandMetadataModal.tsx';

interface BrandCardProps {
  brand: BrandWithUrl;
  cardWidth: number;
  cardHeight: number;
  onBrandUpdated: () => void | Promise<void>;
}

const BrandCard: React.FC<BrandCardProps> = React.memo(
  ({ brand, cardWidth, cardHeight, onBrandUpdated }) => {
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
      useDisclosure(false);

    const { id: brandId, imageId, currentSignedUrl } = brand;

    const { data: brandImageUrl, isLoading: isBrandImageUrlLoading } =
      useBrandImage(brandId, imageId);

    useEffect(() => {
      if (!imageId) {
        setImageSrc(null);
        return;
      }

      const cachedUrl = imageCache.get(brandId, imageId);
      if (cachedUrl) {
        setImageSrc(cachedUrl);
        return;
      }

      if (currentSignedUrl) {
        setImageSrc(currentSignedUrl);
        imageCache.set(brandId, imageId, currentSignedUrl);
        return;
      }

      if (brandImageUrl) {
        setImageSrc(brandImageUrl);
        brand.currentSignedUrl = brandImageUrl;
        imageCache.set(brandId, imageId, brandImageUrl);
      }
    }, [brandId, imageId, brandImageUrl, brand, currentSignedUrl]);

    const handleEditClick = useCallback(() => {
      openEditModal();
    }, [openEditModal]);

    const handleBrandUpdated = useCallback(async () => {
      closeEditModal();
      await onBrandUpdated();
    }, [closeEditModal, onBrandUpdated]);

    return (
      <>
        {editModalOpened && (
          <UpdateBrandMetadataModal
            opened={editModalOpened}
            onClose={closeEditModal}
            originalBrand={brand}
            onSuccess={handleBrandUpdated}
          />
        )}
        <Card
          shadow="sm"
          radius="md"
          padding="0"
          withBorder
          style={{
            width: cardWidth,
            height: cardHeight,
          }}
        >
          <Card.Section
            inheritPadding
            px="0"
            py="0"
            style={{
              position: 'relative',
              height: cardHeight - 84,
              textAlign: 'center',
              overflow: 'hidden',
            }}
          >
            {isBrandImageUrlLoading && (
              <LoadingOverlay
                visible
                zIndex={5}
                overlayProps={{ radius: 'sm', blur: 1 }}
                loaderProps={{
                  color: 'rgb(67,154,255)',
                  type: 'bars',
                  size: 'sm',
                }}
              />
            )}

            {imageSrc ? (
              <img
                src={imageSrc}
                alt={brand.name}
                onError={(e) => {
                  console.error('圖片載入失敗:', imageSrc, e);
                  setImageSrc(null);
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              !isBrandImageUrlLoading && (
                <div
                  style={{
                    width: '100%',
                    height: 96,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999',
                    background: '#f5f5f5',
                  }}
                >
                  無圖片
                </div>
              )
            )}
          </Card.Section>

          <div
            style={{
              padding: '16px 12px',
              height: 84,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <Group
              justify="space-between"
              align="center"
              gap={8}
              style={{ height: 20 }}
            >
              <Text
                fw={700}
                size="16px"
                style={{
                  lineHeight: '20px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {brand.name}
              </Text>

              <ActionIcon
                variant="light"
                size={20}
                color="#228be6"
                aria-label="Edit brand"
                onClick={handleEditClick}
                style={{ flexShrink: 0 }}
              >
                <IconEdit size={14} />
              </ActionIcon>
            </Group>

            <Group gap={8} style={{ height: 16 }}>
              {brand.services.map((service, index) => (
                <Badge
                  key={index}
                  size="xs"
                  color={getServiceColor(service)}
                  autoContrast
                  style={{
                    width: 54,
                    height: 16,
                    padding: '0 6px',
                    fontSize: 10,
                    lineHeight: '12px',
                    fontWeight: 600,
                  }}
                >
                  {getServiceLabel(service)}
                </Badge>
              ))}
            </Group>
          </div>
        </Card>
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.brand.id === nextProps.brand.id &&
      prevProps.brand.name === nextProps.brand.name &&
      prevProps.brand.imageId === nextProps.brand.imageId &&
      JSON.stringify(prevProps.brand.services) ===
        JSON.stringify(nextProps.brand.services) &&
      prevProps.cardWidth === nextProps.cardWidth &&
      prevProps.cardHeight === nextProps.cardHeight
    );
  },
);

BrandCard.displayName = 'BrandCard';

export default BrandCard;
